import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { LancamentoFinanceiro } from '../../../../types/financeiro';

interface GenerateExcelProps {
  lancamentos: LancamentoFinanceiro[];
  periodo: {
    inicio: Date;
    fim: Date;
  };
}

export const generateExcel = async ({ lancamentos, periodo }: GenerateExcelProps) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Relatório Financeiro');

  // Metadados
  workbook.creator = 'Sistema Financeiro';
  workbook.created = new Date();

  // Título do Relatório (Linha 1)
  worksheet.mergeCells('A1:I1');
  const titleCell = worksheet.getCell('A1');
  titleCell.value = `Relatório Financeiro - Período: ${periodo.inicio.toLocaleDateString('pt-BR')} à ${periodo.fim.toLocaleDateString('pt-BR')}`;
  titleCell.font = { name: 'Arial', size: 14, bold: true };
  titleCell.alignment = { vertical: 'middle', horizontal: 'center' };
  titleCell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFFFFFFF' } // Branco para o título principal
  };

  // Configuração das Colunas
  worksheet.columns = [
    { key: 'data', width: 15 },
    { key: 'cliente', width: 35 },
    { key: 'forma', width: 20 },
    { key: 'lancamento', width: 60 }, // Aumentado significativamente
    { key: 'anotacoes', width: 35 },
    { key: 'centro_custo', width: 25 },
    { key: 'debito', width: 18 },
    { key: 'credito', width: 18 },
    { key: 'saldo', width: 18 },
  ];

  // Adicionar Cabeçalho Manualmente na Linha 2
  const headerValues = [
    'Data', 'Cliente/Fornecedor', 'Forma', 'Lançamento', 
    'Anotações', 'Centro de Custo', 'Débito', 'Crédito', 'Saldo'
  ];
  const headerRow = worksheet.getRow(2);
  headerRow.values = headerValues;

  // Estilização do Cabeçalho
  headerRow.font = { name: 'Arial', size: 10, bold: true, color: { argb: 'FF000000' } };
  headerRow.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFE0E0E0' }
  };
  headerRow.alignment = { vertical: 'middle', horizontal: 'center' };
  
  headerRow.eachCell((cell) => {
    cell.border = {
        bottom: { style: 'medium', color: { argb: 'FF999999' } }
    };
  });

  // Variáveis para totais
  let totalReceitas = 0;
  let totalDespesas = 0;

  // Adicionando Dados
  lancamentos.forEach(lancamento => {
    const isDespesa = lancamento.tipo === 'DESPESA';
    const isEstornado = lancamento.situacao !== 'CONFIRMADO';
    
    const valor = parseFloat(lancamento.valor?.toString() || '0');
    const saldo = parseFloat(lancamento.saldo_apos_transacao?.toString() || '0');
    
    // Calcular totais (apenas se confirmado)
    if (!isEstornado) {
        if (isDespesa) {
            totalDespesas += valor;
        } else {
            totalReceitas += valor;
        }
    }
    
    const debito = isDespesa ? valor : null;
    const credito = !isDespesa ? valor : null;
    
    const descricaoFinal = isEstornado 
        ? `[ESTORNADO] ${lancamento.descricao}`
        : lancamento.descricao;

    // Adicionando a linha
    const row = worksheet.addRow({
      data: new Date(lancamento.data_lancamento + 'T00:00:00'),
      cliente: lancamento.pessoa_nome || '-',
      forma: lancamento.forma_pagamento_nome || '-',
      lancamento: descricaoFinal,
      anotacoes: lancamento.observacoes || '',
      centro_custo: lancamento.categoria_nome || '-',
      debito: debito,
      credito: credito,
      saldo: saldo
    });

    // --- Formatação das Células ---
    const rowFont: Partial<ExcelJS.Font> = { name: 'Arial', size: 9 };
    if (isEstornado) {
        rowFont.strike = true;
        rowFont.color = { argb: 'FF999999' };
    }
    row.font = rowFont;

    // Bordas Sutis e Mais Grossas (Medium)
    const borderStyle: Partial<ExcelJS.Borders> = {
        bottom: { style: 'medium', color: { argb: 'FFD9D9D9' } }, // Cor cinza sutil, borda média
        left: { style: 'thin', color: { argb: 'FFEFEFEF' } },
        right: { style: 'thin', color: { argb: 'FFEFEFEF' } }
    };
    
    // Aplicar estilos a todas as células da linha
    row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
        cell.border = borderStyle;
        
        // Alinhamento Centralizado para tudo (Vertical Middle), exceto financeiro que é direita
        // Colunas financeiras são indices 7 (debito), 8 (credito), 9 (saldo)
        if (colNumber >= 7) {
             cell.alignment = { vertical: 'middle', horizontal: 'right' };
        } else {
             cell.alignment = { vertical: 'middle', horizontal: 'center' };
        }
    });

    // Formatação Específica de Colunas
    row.getCell('data').numFmt = 'dd/mm/yyyy';

    // Débito
    const debitoCell = row.getCell('debito');
    debitoCell.numFmt = '"R$ "#,##0.00';
    if (!isEstornado) {
        debitoCell.font = { color: { argb: 'FFFF0000' } }; 
    }

    // Crédito
    const creditoCell = row.getCell('credito');
    creditoCell.numFmt = '"R$ "#,##0.00';
    if (!isEstornado) {
        creditoCell.font = { color: { argb: 'FF00B050' } }; // Verde
    }

    // Saldo
    const saldoCell = row.getCell('saldo');
    saldoCell.numFmt = '"R$ "#,##0.00';
    if (!isEstornado) {
        saldoCell.font = { color: { argb: saldo < 0 ? 'FFFF0000' : 'FF0000FF' } };
    }
  });

  // --- Linha de Totais ---
  const totalRow = worksheet.addRow([
      'TOTAL PERÍODO', // Data (A) -> Merge até Centro Custo (F)
      '', '', '', '', '',
      totalDespesas,   // Debito (G)
      totalReceitas,   // Credito (H)
      (totalReceitas - totalDespesas) // Saldo (Resultado) (I)
  ]);

  // Merge das células de texto do total (A até F)
  // A linha adicionada é a worksheet.lastRow.number
  const lastRowIdx = totalRow.number;
  worksheet.mergeCells(`A${lastRowIdx}:F${lastRowIdx}`);

  // Estilização da Linha de Total
  totalRow.height = 25;
  totalRow.font = { name: 'Arial', size: 11, bold: true };
  totalRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFF0F0F0' } // Cinza bem claro para destaque
  };
  
  // Alinhamento e Bordas do Total
  totalRow.eachCell((cell, colNumber) => {
      cell.border = {
          top: { style: 'medium', color: { argb: 'FF999999' } },
          bottom: { style: 'medium', color: { argb: 'FF999999' } }
      };
      
      if (colNumber === 1) {
          cell.alignment = { vertical: 'middle', horizontal: 'center' }; // Texto "TOTAL PERÍODO"
      } else {
          cell.alignment = { vertical: 'middle', horizontal: 'right' };
          cell.numFmt = '"R$ "#,##0.00';
      }
  });

  // Cores dos valores totais
  const totalDebitoCell = totalRow.getCell(7); // G
  const totalCreditoCell = totalRow.getCell(8); // H
  const totalSaldoCell = totalRow.getCell(9); // I

  totalDebitoCell.font = { name: 'Arial', size: 11, bold: true, color: { argb: 'FFFF0000' } };
  totalCreditoCell.font = { name: 'Arial', size: 11, bold: true, color: { argb: 'FF00B050' } };
  
  const resultado = totalReceitas - totalDespesas;
  totalSaldoCell.font = { 
      name: 'Arial', 
      size: 11, 
      bold: true, 
      color: { argb: resultado < 0 ? 'FFFF0000' : 'FF0000FF' } 
  };

  // Gerar Buffer e Download
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  
  const fileName = `Relatorio_Financeiro_${periodo.inicio.toISOString().split('T')[0]}_${periodo.fim.toISOString().split('T')[0]}.xlsx`;
  saveAs(blob, fileName);
};